import {
  type HseReportType,
  hseMedicalFields,
  hseqRiskRating,
  hseRecordTypeOf,
  NOTIFICATION_RECIPIENTS,
  parseHseReport,
} from '@felix/domain';
import {
  Button,
  Checkbox,
  Group,
  Loader,
  Radio,
  Select,
  Tabs,
  Textarea,
  TextInput,
  Tooltip,
} from '@mantine/core';
import {
  IconCertificate,
  IconChevronRight,
  IconCloudUpload,
  IconCopy,
  IconDeviceFloppy,
  IconExternalLink,
  IconFileDescription,
  IconLink,
  IconLock,
  IconPhoto,
  IconPlus,
  IconPrinter,
  IconSend,
  IconShieldCheck,
  IconShoppingCart,
  IconTool,
  IconTrash,
} from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDateTime, formatDay } from '../../format.js';
import { DayInput } from '../../inputs/DayInput.js';
import { TimeInput } from '../../inputs/TimeInput.js';
import { MasterSectionNote, masterLocked } from '../../master-section.js';
import { Panel } from '../../Panel.js';
import { StatusBadge } from '../../StatusBadge.js';
import { useUnsavedChanges } from '../../shell-context.js';
import table from '../materials/Materials.module.css';
import { recordPdfFileName } from '../reports/record-pdf.js';
import { useRecordPrint } from '../reports/record-print.js';
import { ReturnedBanner } from '../returned/Returned.js';
import {
  ActionDialog,
  CompleteDialog,
  ConfirmDialog,
  FindingDialog,
  NoteDialog,
  ReviewDialog,
  SmsUpdateDialog,
  WorkOrderDialog,
} from './Dialogs.js';
import { Fields, getPath, setPath } from './fields.js';

/** Kaptana ait alan: "Master's Initial Assessment" (ADR 0097 §4; ADR 0127 §1). */
const MASTER_ASSESSMENT_PATH = 'details.initialAssessment';

import { DrillSourceLink } from '../drills/DrillFindings.js';
import {
  emptyReport,
  equipmentSpec,
  generalSpecs,
  initialSpecs,
  investigationSpecs,
  linkSpecs,
  localParts,
  NOTIFICATION_SPECS,
  occurredAtOf,
  RISK_SPECS,
  sideSpecs,
  verificationSpecs,
} from './form-specs.js';
import classes from './Hse.module.css';
import { hsePdf } from './hse-pdf.js';
import {
  CheckMark,
  HseFileItem,
  HseLoadError,
  HsePriorityBadge,
  HseqStages,
  HseStatusBadge,
  HseStepBadge,
  HseTypeIcon,
  hseErrorText,
  hseqSteps,
} from './parts.js';
import type { HseSource } from './source.js';
import type { HseAction, HseDetail, HseFinding, HseOptions, HseReportInput } from './types.js';
import { createHseWorkOrder } from './work-order.js';

/** HSE kapanışının onay türü (ADR 0049 §4): parola isteğe bağlı (ADR 0056 §1). */
const hseReauthKind = (type: HseReportType) => hseRecordTypeOf(type);

type Dialog =
  | { kind: 'review' }
  | { kind: 'action'; action: HseAction | null }
  | { kind: 'complete'; action: HseAction }
  | { kind: 'cancelAction'; action: HseAction }
  | { kind: 'finding'; finding: HseFinding | null }
  | { kind: 'closure' }
  | { kind: 'decide'; decision: 'approved' | 'returned' }
  | { kind: 'reopen' }
  | { kind: 'discard' }
  | { kind: 'note' }
  | { kind: 'workOrder' }
  | { kind: 'sms' }
  | null;

const CLOSURE_GAPS_ALL = [
  'NOT_SUBMITTED',
  'NOT_REVIEWED',
  'NO_ACTIONS',
  'ACTIONS_OPEN',
  'PREVENTIVE_ACTION',
  'ROOT_CAUSE',
  'VERIFICATION_NOTE',
  'EFFECTIVENESS',
  'FINDING_WITHOUT_ACTION',
  'TEST_RESULT',
  'FIT_FOR_SERVICE',
  'LESSONS_LEARNED',
  'RESIDUAL_RISK',
  'INVESTIGATION',
  'NOTIFICATION_DECISION',
  'NOTIFICATION_PENDING',
] as const;

/** Türün kapanışta denetlenen koşulları (ADR 0049 §4, ADR 0097 §5). */
function closureChecks(type: HseReportType): readonly string[] {
  const common = [
    'NOT_SUBMITTED',
    'NOT_REVIEWED',
    'NO_ACTIONS',
    'ACTIONS_OPEN',
    'VERIFICATION_NOTE',
  ];
  const typed: Record<HseReportType, string[]> = {
    non_conformity: ['ROOT_CAUSE', 'PREVENTIVE_ACTION', 'EFFECTIVENESS', 'FINDING_WITHOUT_ACTION'],
    deficiency: ['ROOT_CAUSE', 'TEST_RESULT', 'FIT_FOR_SERVICE'],
    near_miss: ['PREVENTIVE_ACTION', 'LESSONS_LEARNED', 'RESIDUAL_RISK'],
    accident: [
      'ROOT_CAUSE',
      'PREVENTIVE_ACTION',
      'INVESTIGATION',
      'LESSONS_LEARNED',
      'NOTIFICATION_DECISION',
      'NOTIFICATION_PENDING',
    ],
  };
  return CLOSURE_GAPS_ALL.filter((gap) => common.includes(gap) || typed[type].includes(gap));
}

/** Formun 10 basamağı (ortak 2026-10-20b H2; ADR 0097 §4). */
const SECTIONS = [
  'details',
  'initial',
  'risk',
  'investigation',
  'plan',
  'linked',
  'implementation',
  'verification',
  'review',
  'closure',
] as const;
type SectionKey = (typeof SECTIONS)[number];

/** Yeni raporda (taslak) açanın doldurduğu bölümler (ADR 0097 §8). */
const NEW_SECTIONS: readonly string[] = ['details', 'initial', 'risk', 'implementation'];

/** "Raporu Kopyala": olayın tanımı ve türe özgü alanlar; değerlendirme, uygulama, kapanış yok. */
const NOT_COPIED = [
  'initialAssessment',
  'risk',
  'implementation',
  'verification',
  'smsUpdate',
  'notification',
  'testResult',
  'fitForService',
  'residualRisk',
];

export function copyReport(source: HseDetail, tzOffsetMin: number): HseReportInput {
  const details = Object.fromEntries(
    Object.entries(source.report.details ?? {}).filter(([key]) => !NOT_COPIED.includes(key)),
  );
  return emptyReport(source.reportType, tzOffsetMin, {
    title: source.report.title,
    description: source.report.description,
    location: source.report.location,
    department: source.report.department,
    reportedBy: source.report.reportedBy,
    category: source.report.category,
    subCategory: source.report.subCategory,
    priority: source.report.priority,
    source: source.report.source,
    equipmentId: source.report.equipmentId,
    details,
  });
}

export interface HseRecordProps {
  source: HseSource;
  /** Yeni raporun türü; kayıtta türü raporun kendisinden gelir. */
  type: HseReportType | null;
  /** `null`: yeni rapor. */
  id: string | null;
  parentId?: string | null;
  requestId?: string | null;
  /** Arızadan açılan rapor (`?defect=`; ADR 0097 §7). */
  defectId?: string | null;
  /** "Raporu Kopyala" kaynağı (`?copy=`). */
  copyId?: string | null;
  tab?: string | null;
  onBack: () => void;
  onOpen: (id: string, tab?: string | null) => void;
  onNewChild?: (parentId: string) => void;
  /** Başka ekrana geçiş (arıza, iş emri, satın alma talebi; ADR 0097 §7). */
  onNavigate?: (path: string) => void;
  /** Aynı türde kopya taslak (`?copy=`). */
  onCopy?: (type: HseReportType, id: string) => void;
}

const TABS = ['general', 'actions', 'related', 'attachments', 'comments', 'history'] as const;

/**
 * HSEQ raporu (ortak 2026-10-20b `hseq-ekran.jpeg`; ADR 0049, ADR 0097): başlık ve bilgi satırı,
 * dört aşama (Reported, Under Action, Verification, Closed), sekmeler (Form, Düzeltici
 * Faaliyetler, İlgili Eksiklikler, Ekler, Yorumlar, Workflow / Geçmiş). Form 10 basamaktır;
 * kişi yalnız kendi bölümlerini düzenler (H9). Sağda durum, ilgili kayıtlar, hızlı işlemler ve
 * fotoğraflar. Gemide yazılır; ofiste salt okunur, ofis adımı ve not.
 */
export function HseRecord(props: HseRecordProps) {
  const { source } = props;
  const { t } = useTranslation();
  const editor = source.editor;
  const [detail, setDetail] = useState<HseDetail | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [value, setValue] = useState<HseReportInput>(() =>
    emptyReport(props.type ?? 'non_conformity', source.tzOffsetMin ?? 0, {
      parentReportId: props.parentId ?? null,
      defectId: props.defectId ?? null,
    }),
  );
  const [dirty, setDirty] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{
    tone: 'danger' | 'warning' | 'success';
    text: string;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [options, setOptions] = useState<HseOptions | null>(null);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [tab, setTab] = useState<string>(
    props.tab && (TABS as readonly string[]).includes(props.tab) ? props.tab : 'general',
  );
  const printer = useRecordPrint(source);
  useUnsavedChanges(dirty);
  // Yeni rapor kaydedilince kayda geçilir; değişiklik koruyucusu önce temizlenir (yanlış "kaydedilmemiş
  // değişiklik" uyarısı çıkmasın): etkiler bildirim sırasıyla çalışır
  const [openAfterSave, setOpenAfterSave] = useState<string | null>(null);
  const { onOpen } = props;
  useEffect(() => {
    if (openAfterSave && !dirty) onOpen(openAfterSave);
  }, [openAfterSave, dirty, onOpen]);

  const apply = useCallback((next: HseDetail) => {
    setDetail(next);
    setValue({ ...next.report });
    setDirty(false);
    setErrors({});
  }, []);

  const load = useCallback(async () => {
    if (!props.id) return;
    setLoadError(null);
    try {
      apply(await source.detail(props.id));
    } catch (error) {
      setLoadError(error);
    }
  }, [apply, props.id, source]);

  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    if (!editor) return;
    editor
      .options()
      .then(setOptions)
      .catch(() => setOptions(null));
  }, [editor]);

  // "Raporu Kopyala" (H10): kaynağın alanlarıyla yeni taslak; kaydedilene kadar yeni rapordur
  const { copyId, id: recordId } = props;
  useEffect(() => {
    if (!copyId || recordId) return;
    let live = true;
    source
      .detail(copyId)
      .then((from) => {
        if (!live) return;
        setValue(copyReport(from, source.tzOffsetMin ?? from.report.tzOffsetMin));
        setDirty(true);
        setMessage({ tone: 'success', text: t('hse.hseq.copy.loaded', { no: from.reportNo }) });
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [copyId, recordId, source, t]);

  // Arızadan açılan raporda ekipman arızanınkidir (ADR 0097 §7)
  useEffect(() => {
    if (!options || recordId) return;
    setValue((current) => {
      if (!current.defectId || current.equipmentId) return current;
      const defect = options.defects.find((d) => d.id === current.defectId);
      return defect ? { ...current, equipmentId: defect.equipmentId } : current;
    });
  }, [options, recordId]);

  if (props.id && loadError) {
    return <HseLoadError error={loadError} onRetry={() => void load()} />;
  }
  if (props.id && !detail) {
    return (
      <Group justify="center" p="xl">
        <Loader aria-label={t('hse.record.loading')} />
      </Group>
    );
  }

  const type: HseReportType = detail?.reportType ?? value.reportType;
  const can = detail?.can ?? { edit: true, submit: true };
  const isNew = detail === null;
  const canWrite = isNew ? Boolean(editor) : Boolean(editor && can.edit);
  /** Bölümü bu kişi düzenler mi (H9; ADR 0097 §8); eski sunucuda `edit` geçerli. */
  const editable = (section: SectionKey): boolean => {
    if (!canWrite) return false;
    if (isNew) return NEW_SECTIONS.includes(section);
    return can.sections ? can.sections.includes(section) : true;
  };
  const change = (next: HseReportInput) => {
    setValue(next);
    setDirty(true);
  };

  /** Değişikliği uygular; hata metni banner'da. */
  const run = async (work: () => Promise<HseDetail | undefined>, done?: string) => {
    setBusy(true);
    setMessage(null);
    try {
      const next = await work();
      if (next) apply(next);
      if (done) setMessage({ tone: 'success', text: done });
    } catch (error) {
      setMessage({ tone: 'danger', text: hseErrorText(t, error) });
      throw error;
    } finally {
      setBusy(false);
    }
  };

  const validate = (stage: 'draft' | 'submit'): boolean => {
    const parsed = parseHseReport(value, stage);
    const found: Record<string, string> = {};
    if (!value.title?.trim()) found.title = 'REQUIRED';
    if (!parsed.ok) for (const issue of parsed.issues) found[issue.path] = issue.code;
    setErrors(found);
    if (Object.keys(found).length > 0) {
      setMessage({ tone: 'danger', text: t('hse.record.fixFields') });
      return false;
    }
    return true;
  };

  const save = async (submit: boolean) => {
    if (!editor || !validate(submit ? 'submit' : 'draft')) return;
    await run(
      async () => {
        let current = detail;
        if (!current) {
          current = await editor.open(value, props.requestId ?? null);
          setDirty(false);
          if (!submit) {
            setOpenAfterSave(current.id);
            return current;
          }
        } else if (dirty) {
          current = await editor.update(current.id, current.rowVersion, value);
        }
        if (submit && current.status === 'draft') {
          current = await editor.submit(current.id, current.rowVersion);
        }
        if (isNew) setOpenAfterSave(current.id);
        return current;
      },
      t(submit ? 'hse.record.submitted' : 'hse.record.saved'),
    ).catch(() => undefined);
  };

  // Sağlık verisi (ADR 0062 §1): yetkisize alanlar kilitli; yeni raporda yetki seçeneklerden
  // (yüklenene kadar kilitli). Eski sunucu bilgi göndermezse gizleme yok
  const medicalPaths = hseMedicalFields(type).map((key) => `details.${key}`);
  const medicalAllowed = detail
    ? (detail.medical?.hidden ?? []).length === 0 && detail.can.medical !== false
    : options !== null && options.medical !== false;
  const medicalLocked = new Set(medicalAllowed ? [] : medicalPaths);
  // Kaptanın ilk değerlendirmesi (ADR 0127 §1, §2): gemide kaptan ya da vekil yazar; eski
  // sunucuda kilit yok, yazma yolu denetler
  const masterCapacity = editor ? (detail ? detail.can.master : options?.master) : undefined;
  const masterPaths = new Set(masterLocked(masterCapacity) ? [MASTER_ASSESSMENT_PATH] : []);
  const medicalNote =
    medicalPaths.length === 0
      ? null
      : medicalAllowed
        ? t('hse.medical.notice')
        : detail
          ? t('hse.medical.hidden')
          : t('hse.medical.entry');

  const printReport = async () => {
    if (!detail) return;
    const failure = await printer.print((tr, brand) => ({
      definition: hsePdf(tr, {
        detail,
        vessel: { name: detail.vesselName, imo: source.vesselImo ?? null },
        brand,
        printedAt: new Date().toISOString(),
      }),
      fileName: recordPdfFileName(detail.reportNo, t(`hse.types.${type}`)),
    }));
    if (failure) setMessage({ tone: 'danger', text: failure });
  };

  const navigate = (path: string) => props.onNavigate?.(path);
  const local = localParts(value.occurredAt, value.tzOffsetMin);
  const draft = isNew || detail?.status === 'draft';
  const title = isNew ? t(`hse.newTitle.${type}`) : `${detail?.reportNo} · ${detail?.title}`;
  const side = sideSpecs(value, options);
  const fieldsOf = (
    section: SectionKey,
    specs: Parameters<typeof Fields>[0]['specs'],
    columns: 2 | 3 = 2,
  ) => (
    <Fields
      specs={specs}
      value={value}
      onChange={change}
      readOnly={!editable(section)}
      errors={errors}
      columns={columns}
      locked={medicalLocked}
      readOnlyPaths={masterPaths}
    />
  );
  const sectionProps = (key: SectionKey) => ({
    no: SECTIONS.indexOf(key) + 1,
    sectionKey: key,
    // "Salt okunur" işareti gemide (H9); ofiste rapor bütünüyle salt okunurdur
    readOnly:
      Boolean(editor) &&
      key !== 'plan' &&
      key !== 'linked' &&
      key !== 'review' &&
      key !== 'closure' &&
      !editable(key),
  });

  const header = (
    <>
      <nav className={classes.crumbs} aria-label={t('hse.record.breadcrumb')}>
        <button type="button" className={classes.link} onClick={props.onBack}>
          {t('hse.hseq.title')}
        </button>
        <IconChevronRight size={14} aria-hidden />
        <span>{isNew ? t(`hse.newTitle.${type}`) : detail?.reportNo}</span>
      </nav>
      <div className={classes.recordHead}>
        <div className={classes.recordTitle}>
          <HseTypeIcon type={type} />
          <h1>{title}</h1>
          {detail ? <HseStatusBadge status={detail.status} /> : null}
          {detail?.drillSource ? <DrillSourceLink source={detail.drillSource} /> : null}
        </div>
        <div className={classes.recordActions}>
          {editor && draft && (isNew || can.submit) ? (
            <>
              <Button
                variant="default"
                leftSection={<IconDeviceFloppy size={16} />}
                loading={busy}
                onClick={() => void save(false)}
              >
                {t('hse.record.saveDraft')}
              </Button>
              <Button
                leftSection={<IconSend size={16} />}
                loading={busy}
                onClick={() => void save(true)}
              >
                {t('hse.record.submit')}
              </Button>
            </>
          ) : null}
          {editor && !draft && can.edit && dirty ? (
            <Button
              leftSection={<IconDeviceFloppy size={16} />}
              loading={busy}
              onClick={() => void save(false)}
            >
              {t('hse.record.save')}
            </Button>
          ) : null}
          {editor && detail && can.discard ? (
            <Button variant="subtle" color="red" onClick={() => setDialog({ kind: 'discard' })}>
              {t('hse.record.discard')}
            </Button>
          ) : null}
          {editor && can.review ? (
            <Button onClick={() => setDialog({ kind: 'review' })}>{t('hse.record.review')}</Button>
          ) : null}
          {can.decide ? (
            <>
              <Button
                color="green"
                onClick={() => setDialog({ kind: 'decide', decision: 'approved' })}
              >
                {t('hse.closure.approve')}
              </Button>
              <Button
                variant="default"
                onClick={() => setDialog({ kind: 'decide', decision: 'returned' })}
              >
                {t('hse.closure.return')}
              </Button>
            </>
          ) : null}
          {detail && can.export === true ? (
            <Button
              variant="default"
              leftSection={<IconPrinter size={16} />}
              loading={printer.printing}
              disabled={dirty}
              onClick={() => void printReport()}
            >
              {t('recordPdf.print')}
            </Button>
          ) : null}
          {source.office && can.note ? (
            <Button variant="default" onClick={() => setDialog({ kind: 'note' })}>
              {t('hse.notes.add')}
            </Button>
          ) : null}
        </div>
      </div>
      {detail ? (
        <div className={classes.meta}>
          <span>{t('hse.hseq.meta.created', { at: formatDateTime(detail.createdAt) })}</span>
          <span>
            {t('hse.hseq.meta.createdBy', {
              name: detail.names[detail.createdBy] ?? detail.reportedBy ?? '—',
            })}
          </span>
          <span>{t('hse.hseq.meta.type', { type: t(`hse.hseq.typeNames.${type}`) })}</span>
          <span>
            {t('hse.hseq.meta.category', {
              category: detail.category
                ? t(`hse.categories.${type}.${detail.category}`, { defaultValue: detail.category })
                : '—',
            })}
          </span>
          <span>
            {t('hse.hseq.meta.priority')}: <HsePriorityBadge priority={detail.priority} />
          </span>
        </div>
      ) : null}
      {message ? (
        <p
          className={classes.banner}
          data-tone={message.tone}
          role={message.tone === 'danger' ? 'alert' : 'status'}
        >
          {message.text}
        </p>
      ) : null}
    </>
  );

  /* ---------- Formun 10 bölümü ---------- */

  const detailsSection = (
    <Section {...sectionProps('details')}>
      <div className={classes.grid3}>
        <TextInput
          label={t('hse.fields.vessel')}
          required
          readOnly
          value={detail?.vesselName ?? source.vesselName ?? ''}
        />
        <DayInput
          label={t('hse.fields.date')}
          required
          readOnly={!editable('details')}
          value={local.day}
          maxDate={new Date().toISOString().slice(0, 10)}
          error={errors.occurredAt ? t(`hse.fieldErrors.${errors.occurredAt}`) : undefined}
          onChange={(day) =>
            change({ ...value, occurredAt: occurredAtOf(day, local.time, value.tzOffsetMin) })
          }
        />
        <TimeInput
          label={t('hse.fields.time')}
          required
          readOnly={!editable('details')}
          value={local.time}
          onChange={(time) =>
            change({ ...value, occurredAt: occurredAtOf(local.day, time, value.tzOffsetMin) })
          }
        />
      </div>
      {fieldsOf('details', [...generalSpecs(type), equipmentSpec(options)], 3)}
      <Panel
        title={side.key === 'psc' ? t('hse.record.side.psc') : t(`hse.hseq.typeDetails.${type}`)}
        className={classes.card}
      >
        {fieldsOf('details', side.specs)}
        {medicalNote ? (
          <p className={classes.muted} data-medical-note="true">
            {medicalNote}
          </p>
        ) : null}
        {type === 'non_conformity' && value.source === 'psc_inspection' ? (
          <p className={classes.muted}>{t('hse.record.pscHint')}</p>
        ) : null}
      </Panel>
      {detail && type === 'non_conformity' ? <Recurrence detail={detail} /> : null}
    </Section>
  );

  const initialSection = (
    <Section {...sectionProps('initial')}>
      {fieldsOf('initial', initialSpecs(type))}
      <MasterSectionNote
        capacity={masterCapacity}
        author={detail?.initialAssessmentAuthor}
        editable={editable('initial')}
      />
      {detail?.report.reviewedAt ? (
        <p className={classes.banner}>
          {t('hse.review.note')}: {detail.report.reviewNote ?? '—'} ·{' '}
          {detail.names[detail.report.reviewedBy ?? ''] ?? ''} ·{' '}
          {formatDateTime(detail.report.reviewedAt)}
        </p>
      ) : null}
      {type === 'accident' ? (
        <Panel title={t('hse.record.notificationsTitle')} className={classes.card}>
          <div className={classes.cardBody}>
            <p className={classes.muted}>{t('hse.record.notificationsHint')}</p>
            {fieldsOf('initial', NOTIFICATION_SPECS)}
            <Notifications value={value} onChange={change} readOnly={!editable('initial')} />
          </div>
        </Panel>
      ) : null}
    </Section>
  );

  const rating = hseqRiskRating(
    (getPath(value, 'details.risk') ?? null) as { likelihood?: unknown; severity?: unknown },
    detail?.riskMatrix ?? options?.riskMatrix,
  );
  const riskSection = (
    <Section {...sectionProps('risk')}>
      {fieldsOf('risk', RISK_SPECS)}
      <div className={classes.riskLevel}>
        <strong>{t('hse.hseq.risk.level')}:</strong>
        {rating ? (
          <>
            <StatusBadge tone={riskTone(rating.level)}>
              {t(`hse.hseq.risk.levels.${rating.level}`)}
            </StatusBadge>
            <span className={classes.muted}>
              {t('hse.hseq.risk.score', { score: rating.score })}
            </span>
          </>
        ) : (
          <span className={classes.muted}>{t('hse.hseq.risk.notRated')}</span>
        )}
        <span className={classes.muted}>{t('hse.hseq.risk.matrixHint')}</span>
      </div>
    </Section>
  );

  const investigationSection = (
    <Section {...sectionProps('investigation')}>
      {fieldsOf('investigation', investigationSpecs(type, options))}
    </Section>
  );

  const planSection = (
    <Section {...sectionProps('plan')}>
      {detail && type === 'non_conformity' ? (
        <FindingsCard
          detail={detail}
          canEdit={Boolean(editor && can.findings)}
          onAdd={() => setDialog({ kind: 'finding', finding: null })}
          onView={(finding) => setDialog({ kind: 'finding', finding })}
        />
      ) : null}
      {detail ? (
        <PlanTable
          detail={detail}
          canAdd={Boolean(editor && detail.can.actions)}
          mine={new Set(editor ? (detail.can.actionIds ?? []) : [])}
          onAdd={() => setDialog({ kind: 'action', action: null })}
          onEdit={(action) => setDialog({ kind: 'action', action })}
        />
      ) : (
        <p className={classes.muted}>{t('hse.actions.afterSubmit')}</p>
      )}
    </Section>
  );

  const linkedSection = (
    <Section {...sectionProps('linked')}>
      <LinkedRecords detail={detail} onNavigate={props.onNavigate} onOpen={props.onOpen} />
      {editor && !isNew
        ? fieldsOf(
            'investigation',
            linkSpecs(options).filter((spec) => spec.path !== 'defectId' || !detail?.links.defect),
          )
        : null}
    </Section>
  );

  const implementationSection = (
    <Section {...sectionProps('implementation')}>
      {fieldsOf('implementation', [
        { path: 'details.implementation.workDone', kind: 'textarea', full: true, rows: 3 },
      ])}
      <Materials value={value} onChange={change} readOnly={!editable('implementation')} />
      <Evidence detail={detail} source={source} />
    </Section>
  );

  const effective = getPath(value, 'details.verification.effective');
  const verificationSection = (
    <Section {...sectionProps('verification')}>
      <Radio.Group
        label={t('hse.hseq.verification.effective')}
        value={typeof effective === 'string' ? effective : ''}
        onChange={(next) =>
          editable('verification')
            ? change(setPath(value, 'details.verification.effective', next || null))
            : undefined
        }
        error={
          errors['details.verification.effective']
            ? t(`hse.fieldErrors.${errors['details.verification.effective']}`)
            : undefined
        }
      >
        <div className={classes.radioRow}>
          <Radio
            value="yes"
            label={t('hse.hseq.verification.yes')}
            disabled={!editable('verification')}
          />
          <Radio
            value="no"
            label={t('hse.hseq.verification.no')}
            disabled={!editable('verification')}
          />
        </div>
      </Radio.Group>
      {fieldsOf('verification', verificationSpecs(type))}
    </Section>
  );

  const reviewSection = (
    <Section {...sectionProps('review')}>
      <ManagementReview detail={detail} />
      {source.office && detail && can.note ? (
        <Group>
          <Button variant="default" onClick={() => setDialog({ kind: 'note' })}>
            {t('hse.notes.add')}
          </Button>
        </Group>
      ) : null}
    </Section>
  );

  const closureSection = (
    <Section {...sectionProps('closure')}>
      {detail ? (
        <ClosurePanel
          detail={detail}
          dirty={dirty}
          onClosure={() => setDialog({ kind: 'closure' })}
          onReopen={editor && can.reopen ? () => setDialog({ kind: 'reopen' }) : undefined}
        />
      ) : (
        <p className={classes.muted}>{t('hse.hseq.saveFirst')}</p>
      )}
    </Section>
  );

  const actionsCard = detail ? (
    <ActionsCard
      detail={detail}
      source={source}
      busy={busy}
      onAdd={() => setDialog({ kind: 'action', action: null })}
      onEdit={(action) => setDialog({ kind: 'action', action })}
      onStart={(action) =>
        void run(
          () =>
            editor?.changeAction(action.id, action.rowVersion, { op: 'start' }) ??
            Promise.resolve(undefined),
        ).catch(() => undefined)
      }
      onComplete={(action) => setDialog({ kind: 'complete', action })}
      onCancel={(action) => setDialog({ kind: 'cancelAction', action })}
      onUpload={(action, file) =>
        void run(async () => {
          await editor?.upload('hse_action', action.id, file);
          return source.detail(detail.id);
        }, t('hse.files.uploaded')).catch(() => undefined)
      }
      onClosure={() => setDialog({ kind: 'closure' })}
    />
  ) : (
    <Panel title={t('hse.actions.title')}>
      <p className={classes.muted}>{t('hse.actions.afterSubmit')}</p>
    </Panel>
  );

  const closeGaps = (detail?.gaps ?? []).map((gap) =>
    t(`hse.closure.gaps.${gap}`, { defaultValue: gap }),
  );
  const rail = (
    <aside className={classes.rail} aria-label={t('hse.record.rail')}>
      <StatusTimeline detail={detail} />
      <RelatedRecords detail={detail} onNavigate={props.onNavigate} />
      {editor ? (
        <QuickActions
          detail={detail}
          busy={busy}
          closeGaps={closeGaps}
          onDefect={() => detail && navigate(`/pms/defects?new=1&hse=${detail.id}`)}
          onWorkOrder={() => setDialog({ kind: 'workOrder' })}
          onRequisition={() =>
            detail && navigate(`/purchasing/requisitions?new=1&hse=${detail.id}`)
          }
          onSms={() => setDialog({ kind: 'sms' })}
          onCopy={() => detail && props.onCopy?.(type, detail.id)}
          onClose={() =>
            detail?.can.closure
              ? setDialog({ kind: 'closure' })
              : setDialog({ kind: 'decide', decision: 'approved' })
          }
        />
      ) : null}
      <PhotosCard
        detail={detail}
        source={source}
        busy={busy}
        onUpload={(file) =>
          detail
            ? void run(async () => {
                await editor?.upload('hse_report', detail.id, file);
                return source.detail(detail.id);
              }, t('hse.files.uploaded')).catch(() => undefined)
            : undefined
        }
      />
    </aside>
  );

  return (
    <div className={classes.screen}>
      {header}
      <HseqStages steps={hseqSteps(detail, detail?.status ?? 'draft')} />
      <div className={classes.layout}>
        <div className={classes.main}>
          <Tabs
            value={tab}
            onChange={(next) => setTab(next ?? 'general')}
            keepMounted={false}
            classNames={{ list: classes.tabsList }}
          >
            <Tabs.List>
              <Tabs.Tab value="general">{t('hse.tabs.general')}</Tabs.Tab>
              <Tabs.Tab value="actions">{t('hse.tabs.actions')}</Tabs.Tab>
              {type === 'non_conformity' ? (
                <Tabs.Tab value="related">{t('hse.tabs.related')}</Tabs.Tab>
              ) : null}
              <Tabs.Tab value="attachments">{t('hse.tabs.attachments')}</Tabs.Tab>
              <Tabs.Tab value="comments">{t('hse.tabs.comments')}</Tabs.Tab>
              <Tabs.Tab value="history">{t('hse.tabs.history')}</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="general" pt="md">
              <div className={classes.main}>
                {detailsSection}
                {initialSection}
                {riskSection}
                {investigationSection}
                {planSection}
                {linkedSection}
                {implementationSection}
                {verificationSection}
                {reviewSection}
                {closureSection}
              </div>
            </Tabs.Panel>

            <Tabs.Panel value="actions" pt="md">
              <div className={classes.main}>{actionsCard}</div>
            </Tabs.Panel>

            {type === 'non_conformity' ? (
              <Tabs.Panel value="related" pt="md">
                <RelatedReports
                  detail={detail}
                  canCreate={Boolean(editor && detail && detail.status !== 'draft')}
                  onOpen={(id) => props.onOpen(id)}
                  {...(props.onNewChild ? { onNewChild: props.onNewChild } : {})}
                />
              </Tabs.Panel>
            ) : null}

            <Tabs.Panel value="attachments" pt="md">
              <AttachmentsTab detail={detail} source={source} />
            </Tabs.Panel>

            <Tabs.Panel value="comments" pt="md">
              <CommentsTab
                detail={detail}
                canComment={Boolean(editor && can.comment)}
                onComment={(body) =>
                  detail
                    ? run(() => editor?.comment(detail.id, body) ?? Promise.resolve(undefined))
                    : Promise.resolve(undefined)
                }
              />
            </Tabs.Panel>

            <Tabs.Panel value="history" pt="md">
              <HistoryTab detail={detail} />
            </Tabs.Panel>
          </Tabs>
        </div>
        {rail}
      </div>

      {detail && editor ? (
        <>
          <ReviewDialog
            key={`review-${detail.rowVersion}`}
            opened={dialog?.kind === 'review'}
            initial={{
              priority: detail.priority,
              responsible: detail.responsible,
              targetDate: detail.targetDate,
            }}
            onClose={() => setDialog(null)}
            onConfirm={(input) =>
              run(() => editor.review(detail.id, { rowVersion: detail.rowVersion, ...input }))
            }
          />
          {dialog?.kind === 'action' ? (
            <ActionDialog
              opened
              action={dialog.action}
              findings={detail.findings}
              options={options}
              onClose={() => setDialog(null)}
              onConfirm={(input) =>
                run(() =>
                  dialog.action
                    ? editor.changeAction(dialog.action.id, dialog.action.rowVersion, {
                        op: 'edit',
                        input,
                      })
                    : editor.addAction(detail.id, input),
                )
              }
            />
          ) : null}
          {dialog?.kind === 'complete' ? (
            <CompleteDialog
              opened
              action={dialog.action}
              options={options}
              onClose={() => setDialog(null)}
              onConfirm={(input) =>
                run(() =>
                  editor.changeAction(dialog.action.id, dialog.action.rowVersion, {
                    op: 'complete',
                    ...input,
                  }),
                )
              }
            />
          ) : null}
          <ConfirmDialog
            opened={dialog?.kind === 'cancelAction'}
            title={t('hse.actions.cancelTitle')}
            body={t('hse.actions.cancelBody')}
            reason="required"
            password={false}
            confirmLabel={t('hse.actions.cancel')}
            color="red"
            onClose={() => setDialog(null)}
            onConfirm={({ reason }) =>
              dialog?.kind === 'cancelAction'
                ? run(() =>
                    editor.changeAction(dialog.action.id, dialog.action.rowVersion, {
                      op: 'cancel',
                      reason,
                    }),
                  )
                : Promise.resolve(undefined)
            }
          />
          {dialog?.kind === 'finding' ? (
            <FindingDialog
              opened
              finding={dialog.finding}
              onClose={() => setDialog(null)}
              onConfirm={(input) =>
                run(() =>
                  dialog.finding
                    ? editor.updateFinding(dialog.finding.id, dialog.finding.rowVersion, input)
                    : editor.addFinding(detail.id, input),
                )
              }
            />
          ) : null}
          {dialog?.kind === 'workOrder' ? (
            <WorkOrderDialog
              opened
              detail={detail}
              options={options}
              onClose={() => setDialog(null)}
              onOpenWorkOrder={(id) => navigate(`/pms/work-orders?wo=${id}`)}
              onConfirm={async (input) => {
                if (!editor.workOrder) return;
                const { detail: next, dateDropped } = await createHseWorkOrder(
                  editor.workOrder.bind(editor),
                  detail.id,
                  input,
                );
                apply(next);
                const result = next.workOrderResult;
                if (result) {
                  setMessage({
                    tone: dateDropped ? 'warning' : 'success',
                    text: t(
                      dateDropped
                        ? 'hse.hseq.workOrder.dateDropped'
                        : result.created
                          ? 'hse.hseq.workOrder.created'
                          : 'hse.hseq.workOrder.reused',
                      {
                        no: result.no,
                      },
                    ),
                  });
                }
              }}
            />
          ) : null}
          {dialog?.kind === 'sms' ? (
            <SmsUpdateDialog
              opened
              initial={(getPath(value, 'details.smsUpdate') ?? null) as never}
              readOnly={!can.edit || dirty}
              onClose={() => setDialog(null)}
              onConfirm={(sms) =>
                run(
                  () =>
                    editor.update(
                      detail.id,
                      detail.rowVersion,
                      setPath(value, 'details.smsUpdate', sms),
                    ),
                  t('hse.hseq.sms.saved'),
                )
              }
            />
          ) : null}
          <ConfirmDialog
            opened={dialog?.kind === 'closure'}
            title={t('hse.closure.submitTitle')}
            body={t('hse.closure.submitBody')}
            reason="none"
            password
            reauthKind={hseReauthKind(detail.reportType)}
            confirmLabel={t('hse.closure.submit')}
            onClose={() => setDialog(null)}
            onConfirm={({ password }) =>
              run(() => editor.closure(detail.id, password === '' ? undefined : password))
            }
          />
          <ConfirmDialog
            opened={dialog?.kind === 'reopen'}
            title={t('hse.closure.reopenTitle')}
            body={t('hse.closure.reopenBody')}
            reason="required"
            password
            reauthKind={hseReauthKind(detail.reportType)}
            confirmLabel={t('hse.closure.reopen')}
            color="red"
            onClose={() => setDialog(null)}
            onConfirm={({ reason, password }) =>
              run(() =>
                editor.reopen(detail.id, {
                  ...(password ? { password } : {}),
                  reason,
                  contentHash: detail.hash,
                }),
              )
            }
          />
          <ConfirmDialog
            opened={dialog?.kind === 'discard'}
            title={t('hse.record.discardTitle')}
            body={t('hse.record.discardBody')}
            reason="required"
            password={false}
            confirmLabel={t('hse.record.discard')}
            color="red"
            onClose={() => setDialog(null)}
            onConfirm={async ({ reason }) => {
              await editor.discard(detail.id, detail.rowVersion, reason);
              setDirty(false);
              props.onBack();
            }}
          />
        </>
      ) : null}
      {detail && dialog?.kind === 'decide' && detail.pending ? (
        <ConfirmDialog
          opened
          title={t(
            dialog.decision === 'approved' ? 'hse.closure.approveTitle' : 'hse.closure.returnTitle',
          )}
          body={t(
            dialog.decision === 'approved' ? 'hse.closure.approveBody' : 'hse.closure.returnBody',
          )}
          reason={dialog.decision === 'returned' ? 'required' : 'optional'}
          password
          reauthKind={hseReauthKind(detail.reportType)}
          confirmLabel={t(
            dialog.decision === 'approved' ? 'hse.closure.approve' : 'hse.closure.return',
          )}
          {...(dialog.decision === 'approved' ? { color: 'green' } : {})}
          onClose={() => setDialog(null)}
          onConfirm={({ reason, password }) => {
            const pending = detail.pending;
            if (!pending) return Promise.resolve(undefined);
            const input = {
              ...(password ? { password } : {}),
              decision: dialog.decision,
              reason: reason || null,
              expected: {
                submissionId: pending.submissionId,
                step: pending.step,
                contentHash: detail.hash,
              },
            };
            return run(
              () =>
                (pending.office
                  ? source.office?.decide(detail.id, input)
                  : editor?.decide(detail.id, input)) ?? Promise.resolve(undefined),
            );
          }}
        />
      ) : null}
      {detail && source.office ? (
        <NoteDialog
          opened={dialog?.kind === 'note'}
          onClose={() => setDialog(null)}
          onConfirm={(input) =>
            run(() => source.office?.note(detail.id, input) ?? Promise.resolve(undefined))
          }
        />
      ) : null}
    </div>
  );
}

/* ---------- HSEQ parçaları (ADR 0097) ---------- */

function riskTone(level: string): 'success' | 'warning' | 'danger' | 'info' {
  switch (level) {
    case 'very_low':
    case 'low':
      return 'success';
    case 'medium':
      return 'warning';
    default:
      return 'danger';
  }
}

/** Numaralı bölüm: başlık, alt açıklama ve salt okunur işareti. */
function Section({
  no,
  sectionKey,
  readOnly,
  children,
}: {
  no: number;
  sectionKey: SectionKey;
  readOnly: boolean;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const id = `hseq-section-${sectionKey}`;
  return (
    <section className={classes.section} aria-labelledby={id} data-section={sectionKey}>
      <div className={classes.sectionHead}>
        <span className={classes.sectionNo} aria-hidden>
          {no}
        </span>
        <h2 id={id}>{t(`hse.hseq.sections.${sectionKey}`)}</h2>
        <span className={classes.sectionHint}>{t(`hse.hseq.sectionHints.${sectionKey}`)}</span>
        {readOnly ? (
          <span className={classes.sectionTag}>
            <IconLock size={12} aria-hidden /> {t('hse.hseq.readOnly')}
          </span>
        ) : null}
      </div>
      <div className={classes.sectionBody}>{children}</div>
    </section>
  );
}

/** Tekrar eden uygunsuzluk kontrolü (H5): aynı gemide aynı madde. */
function Recurrence({ detail }: { detail: HseDetail }) {
  const { t } = useTranslation();
  const items = detail.recurrence ?? [];
  return (
    <div className={classes.banner} data-tone={items.length > 0 ? 'danger' : undefined}>
      <strong>{t('hse.hseq.recurrence.title')}: </strong>
      {items.length === 0
        ? t('hse.hseq.recurrence.none')
        : `${t('hse.hseq.recurrence.found', { count: items.length })} ${items
            .map((r) => `${r.reportNo} (${r.title})`)
            .join(', ')}`}
    </div>
  );
}

/** 5. Corrective Action Plan: No, faaliyet, sorumlu, hedef tarih, durum; "Faaliyet Ekle". */
function PlanTable({
  detail,
  canAdd,
  mine,
  onAdd,
  onEdit,
}: {
  detail: HseDetail;
  canAdd: boolean;
  mine: ReadonlySet<string>;
  onAdd: () => void;
  onEdit: (action: HseAction) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className={classes.cardBody}>
      {detail.actionList.length === 0 ? (
        <p className={classes.muted}>
          {t(detail.report.reviewedAt ? 'hse.actions.empty' : 'hse.actions.afterReview')}
        </p>
      ) : (
        <section
          className={table.tableWrap}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
          tabIndex={0}
          aria-label={t('hse.hseq.sections.plan')}
        >
          <table className={`${table.table} ${classes.table}`}>
            <thead>
              <tr>
                <th scope="col">{t('hse.actions.no')}</th>
                <th scope="col">{t('hse.actions.description')}</th>
                <th scope="col">{t('hse.actions.responsible')}</th>
                <th scope="col">{t('hse.actions.dueDate')}</th>
                <th scope="col">{t('hse.actions.status')}</th>
              </tr>
            </thead>
            <tbody>
              {detail.actionList.map((action) => (
                <tr key={action.id}>
                  <td>{action.actionNo}</td>
                  <td className={classes.wrap}>
                    {mine.has(action.id) && action.status === 'planned' ? (
                      <button type="button" className={classes.link} onClick={() => onEdit(action)}>
                        {action.description}
                      </button>
                    ) : (
                      action.description
                    )}
                    <div className={classes.muted}>{t(`hse.actionKinds.${action.kind}`)}</div>
                  </td>
                  <td>{action.responsible}</td>
                  <td>
                    {formatDay(action.dueDate)}
                    {action.overdue ? (
                      <div className={classes.muted}>{t('hse.list.overdue')}</div>
                    ) : null}
                  </td>
                  <td>
                    <HseStepBadge status={action.status} kind="action" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
      {canAdd ? (
        <Group>
          <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={onAdd}>
            {t('hse.actions.new')}
          </Button>
        </Group>
      ) : null}
    </div>
  );
}

type MaterialRow = {
  item: string | null;
  code: string | null;
  quantity: number | null;
  unit: string | null;
  fromStock: boolean | null;
  requisitionNo: string | null;
};

/** 7. Implementation ▸ Kullanılan Malzemeler: parça / kod, miktar, stoktan mı, satın alma. */
function Materials({
  value,
  onChange,
  readOnly,
}: {
  value: HseReportInput;
  onChange: (next: HseReportInput) => void;
  readOnly: boolean;
}) {
  const { t } = useTranslation();
  const items =
    (getPath(value, 'details.implementation.materials') as MaterialRow[] | undefined) ?? [];
  const set = (next: MaterialRow[]) =>
    onChange(setPath(value, 'details.implementation.materials', next));
  const patch = (index: number, part: Partial<MaterialRow>) =>
    set(items.map((item, i) => (i === index ? { ...item, ...part } : item)));
  return (
    <div className={classes.materials}>
      <div className={classes.label}>{t('hse.hseq.implementation.materials')}</div>
      {items.length === 0 && readOnly ? (
        <p className={classes.muted}>{t('hse.hseq.implementation.empty')}</p>
      ) : null}
      {items.map((item, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: malzeme satırının kimliği sırasıdır
        <div key={index} className={classes.materialRow}>
          <TextInput
            label={t('hse.hseq.implementation.item')}
            value={item.item ?? ''}
            readOnly={readOnly}
            maxLength={200}
            onChange={(event) => patch(index, { item: event.currentTarget.value || null })}
          />
          <TextInput
            label={t('hse.hseq.implementation.code')}
            value={item.code ?? ''}
            readOnly={readOnly}
            maxLength={100}
            onChange={(event) => patch(index, { code: event.currentTarget.value || null })}
          />
          <TextInput
            label={t('hse.hseq.implementation.quantity')}
            value={
              item.quantity === null || item.quantity === undefined ? '' : String(item.quantity)
            }
            readOnly={readOnly}
            inputMode="decimal"
            onChange={(event) => {
              const text = event.currentTarget.value.replace(',', '.');
              const number = Number(text);
              patch(index, { quantity: text === '' || Number.isNaN(number) ? null : number });
            }}
          />
          <Checkbox
            label={t('hse.hseq.implementation.fromStock')}
            checked={item.fromStock === true}
            disabled={readOnly}
            mb={10}
            onChange={(event) => patch(index, { fromStock: event.currentTarget.checked })}
          />
          <TextInput
            label={t('hse.hseq.implementation.requisition')}
            value={item.requisitionNo ?? ''}
            readOnly={readOnly}
            maxLength={100}
            onChange={(event) => patch(index, { requisitionNo: event.currentTarget.value || null })}
          />
          {!readOnly ? (
            <Button
              variant="subtle"
              color="red"
              aria-label={t('hse.hseq.implementation.remove')}
              onClick={() => set(items.filter((_, i) => i !== index))}
            >
              <IconTrash size={16} aria-hidden />
            </Button>
          ) : (
            <span />
          )}
        </div>
      ))}
      {!readOnly ? (
        <Group>
          <Button
            size="xs"
            variant="light"
            leftSection={<IconPlus size={14} />}
            onClick={() =>
              set([
                ...items,
                {
                  item: null,
                  code: null,
                  quantity: null,
                  unit: null,
                  fromStock: null,
                  requisitionNo: null,
                },
              ])
            }
          >
            {t('hse.hseq.implementation.add')}
          </Button>
        </Group>
      ) : null}
    </div>
  );
}

/** 7. Implementation ▸ Kanıtlar: raporun ve faaliyetlerin ekleri. */
function Evidence({ detail, source }: { detail: HseDetail | null; source: HseSource }) {
  const { t } = useTranslation();
  const files = detail ? [...detail.files, ...detail.actionList.flatMap((a) => a.files)] : [];
  return (
    <div>
      <div className={classes.label}>{t('hse.hseq.implementation.evidence')}</div>
      {files.length === 0 ? (
        <p className={classes.muted}>{t('hse.hseq.implementation.noEvidence')}</p>
      ) : (
        <ul className={classes.files}>
          {files.map((file) => (
            <HseFileItem key={file.id} file={file} href={source.office?.fileUrl(file.id)} />
          ))}
        </ul>
      )}
    </div>
  );
}

/** 9. Management / DPA Review: kapanış onay adımları, kararlar ve ofis notları. */
function ManagementReview({ detail }: { detail: HseDetail | null }) {
  const { t } = useTranslation();
  if (!detail) return <p className={classes.muted}>{t('hse.hseq.saveFirst')}</p>;
  const notes = detail.officeNotes;
  return (
    <div className={classes.cardBody}>
      <ApprovalSteps detail={detail} />
      <div>
        <div className={classes.label}>{t('hse.hseq.review.officeNotes')}</div>
        {notes.length === 0 ? (
          <p className={classes.muted}>{t('hse.hseq.review.empty')}</p>
        ) : (
          <ul className={classes.timeline}>
            {notes.map((note) => (
              <li key={note.id}>
                <span>
                  {formatDateTime(note.at)}
                  <div className={classes.muted}>{detail.names[note.by] ?? ''}</div>
                </span>
                <div>
                  <strong>{t(`hse.noteKinds.${note.kind}`)}</strong>
                  <div className={classes.timelineDetail}>{note.body}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/** 6. Linked Records: arıza, iş emri, satın alma talebi, ekipman ve ilgili raporlar. */
function LinkedRecords({
  detail,
  onNavigate,
  onOpen,
}: {
  detail: HseDetail | null;
  onNavigate: ((path: string) => void) | undefined;
  onOpen: (id: string) => void;
}) {
  const { t } = useTranslation();
  if (!detail) return <p className={classes.muted}>{t('hse.hseq.saveFirst')}</p>;
  const { links, related } = detail;
  const items: { key: string; icon: ReactNode; label: string; value: ReactNode }[] = [
    {
      key: 'defect',
      icon: <IconTool size={18} />,
      label: t('hse.hseq.linked.defect'),
      value: links.defect ? (
        <LinkButton
          text={links.defect.no}
          onClick={onNavigate ? () => onNavigate(`/pms/defects?id=${links.defect?.id}`) : undefined}
        />
      ) : (
        t('hse.hseq.linked.none')
      ),
    },
    {
      key: 'workOrder',
      icon: <IconFileDescription size={18} />,
      label: t('hse.hseq.linked.workOrder'),
      value: links.workOrder ? (
        <LinkButton
          text={links.workOrder.no}
          onClick={
            onNavigate ? () => onNavigate(`/pms/work-orders?wo=${links.workOrder?.id}`) : undefined
          }
        />
      ) : (
        t('hse.hseq.linked.none')
      ),
    },
    {
      key: 'requisition',
      icon: <IconShoppingCart size={18} />,
      label: t('hse.hseq.linked.requisition'),
      value:
        (links.requisitions ?? []).length > 0
          ? (links.requisitions ?? []).map((r) => r.no).join(', ')
          : t('hse.hseq.linked.none'),
    },
    {
      key: 'equipment',
      icon: <IconCertificate size={18} />,
      label: t('hse.hseq.linked.equipment'),
      value: links.equipment
        ? `${links.equipment.code} · ${links.equipment.name}`
        : t('hse.hseq.linked.none'),
    },
  ];
  return (
    <div className={classes.cardBody}>
      <div className={classes.linkedList}>
        {items.map((item) => (
          <div key={item.key} className={classes.linkedItem}>
            <span className={classes.linkIcon} aria-hidden>
              {item.icon}
            </span>
            <div>
              <strong>{item.label}</strong>
              <span>{item.value}</span>
            </div>
          </div>
        ))}
      </div>
      {related.parent || related.children.length > 0 ? (
        <ul className={classes.links}>
          {related.parent ? (
            <li>
              <span className={classes.linkIcon} aria-hidden>
                <IconLink size={16} />
              </span>
              <span>{t('hse.hseq.linked.parent')}: </span>
              <button
                type="button"
                className={classes.link}
                onClick={() => related.parent && onOpen(related.parent.id)}
              >
                {related.parent.reportNo} · {related.parent.title}
              </button>
            </li>
          ) : null}
          {related.children.map((child) => (
            <li key={child.id}>
              <span className={classes.linkIcon} aria-hidden>
                <IconLink size={16} />
              </span>
              <button type="button" className={classes.link} onClick={() => onOpen(child.id)}>
                {child.reportNo} · {child.title}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function LinkButton({ text, onClick }: { text: string; onClick: (() => void) | undefined }) {
  return onClick ? (
    <button type="button" className={classes.link} onClick={onClick}>
      {text}
    </button>
  ) : (
    <>{text}</>
  );
}

/** Sağ sütun "Durum / Status": dört aşama, zaman ve kişi. */
function StatusTimeline({ detail }: { detail: HseDetail | null }) {
  const { t } = useTranslation();
  const steps = hseqSteps(detail, detail?.status ?? 'draft');
  return (
    <Panel title={t('hse.hseq.timeline.title')}>
      <ol className={classes.statusList}>
        {steps.map((step, index) => (
          <li key={step.stage} className={classes.statusItem} data-state={step.state}>
            <span aria-hidden>{step.state === 'done' ? '✓' : index + 1}</span>
            <div>
              <strong>{t(`hse.hseq.stages.${step.stage}`)}</strong>
              <div className={classes.muted}>
                {step.at
                  ? `${formatDateTime(step.at)}${step.by && detail?.names[step.by] ? ` – ${detail.names[step.by]}` : ''}`
                  : step.state === 'current'
                    ? t('hse.hseq.timeline.waiting')
                    : '—'}
              </div>
            </div>
          </li>
        ))}
      </ol>
    </Panel>
  );
}

/** Sağ sütun "İlgili Kayıtlar": arıza, iş emri, satın alma ve ekipman, durumlarıyla. */
function RelatedRecords({
  detail,
  onNavigate,
}: {
  detail: HseDetail | null;
  onNavigate: ((path: string) => void) | undefined;
}) {
  const { t } = useTranslation();
  if (!detail) {
    return (
      <Panel title={t('hse.links.title')}>
        <p className={classes.muted}>{t('hse.links.empty')}</p>
      </Panel>
    );
  }
  const { links } = detail;
  const rows: {
    key: string;
    icon: ReactNode;
    label: string;
    no: string | null;
    path?: string;
    badge?: ReactNode;
  }[] = [
    {
      key: 'defect',
      icon: <IconTool size={18} />,
      label: t('hse.hseq.linked.defect'),
      no: links.defect?.no ?? null,
      ...(links.defect ? { path: `/pms/defects?id=${links.defect.id}` } : {}),
      badge: links.defect ? (
        <StatusBadge tone="success">{t('hse.hseq.linked.linked')}</StatusBadge>
      ) : null,
    },
    {
      key: 'workOrder',
      icon: <IconFileDescription size={18} />,
      label: t('hse.hseq.linked.workOrder'),
      no: links.workOrder?.no ?? null,
      ...(links.workOrder ? { path: `/pms/work-orders?wo=${links.workOrder.id}` } : {}),
      badge: links.workOrder?.status ? (
        <StatusBadge tone={links.workOrder.status === 'cancelled' ? 'neutral' : 'warning'}>
          {t(`pms.work.status.${links.workOrder.status}`, {
            defaultValue: links.workOrder.status,
          })}
        </StatusBadge>
      ) : null,
    },
    {
      key: 'requisition',
      icon: <IconShoppingCart size={18} />,
      label: t('hse.hseq.linked.requisition'),
      no: (links.requisitions ?? []).map((r) => r.no).join(', ') || null,
    },
    {
      key: 'equipment',
      icon: <IconCertificate size={18} />,
      label: t('hse.hseq.linked.equipment'),
      no: links.equipment ? `${links.equipment.code} · ${links.equipment.name}` : null,
    },
  ];
  return (
    <Panel title={t('hse.links.title')}>
      <ul className={classes.links}>
        {rows.map((row) => (
          <li key={row.key}>
            <span className={classes.linkIcon} aria-hidden>
              {row.icon}
            </span>
            <span>
              <strong>{row.label}</strong>
              <br />
              {row.no && row.path && onNavigate ? (
                <button
                  type="button"
                  className={classes.link}
                  onClick={() => row.path && onNavigate(row.path)}
                >
                  {row.no}
                </button>
              ) : (
                <span className={classes.muted}>{row.no ?? t('hse.hseq.linked.none')}</span>
              )}
            </span>
            {row.badge ?? null}
          </li>
        ))}
      </ul>
    </Panel>
  );
}

/** Dar sağ sütunda uzun etiket satır kırar (ör. "Create Purchase Requisition"). */
const QUICK = { root: classes.quickButton, label: classes.quickLabel };

/** Sağ sütun "Hızlı İşlemler" (H10): gönderilmiş raporda gemide. */
function QuickActions({
  detail,
  busy,
  closeGaps,
  onDefect,
  onWorkOrder,
  onRequisition,
  onSms,
  onCopy,
  onClose,
}: {
  detail: HseDetail | null;
  busy: boolean;
  closeGaps: readonly string[];
  onDefect: () => void;
  onWorkOrder: () => void;
  onRequisition: () => void;
  onSms: () => void;
  onCopy: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const submitted = detail !== null && detail.status !== 'draft';
  const closable = Boolean(detail?.can.closure || detail?.can.decide);
  const close = (
    <Button
      variant="default"
      fullWidth
      classNames={QUICK}
      leftSection={<IconShieldCheck size={16} />}
      disabled={!closable || busy}
      onClick={onClose}
    >
      {t('hse.hseq.quick.close')}
    </Button>
  );
  return (
    <Panel title={t('hse.hseq.quick.title')}>
      {!submitted ? (
        <p className={classes.muted}>{t('hse.hseq.quick.afterSubmit')}</p>
      ) : (
        <div className={classes.quick}>
          <Button
            variant="default"
            fullWidth
            classNames={QUICK}
            leftSection={<IconTool size={16} />}
            onClick={onDefect}
          >
            {t('hse.hseq.quick.createDefect')}
          </Button>
          <Button
            variant="default"
            fullWidth
            classNames={QUICK}
            leftSection={<IconFileDescription size={16} />}
            disabled={busy}
            onClick={onWorkOrder}
          >
            {t('hse.hseq.quick.createWorkOrder')}
          </Button>
          <Button
            variant="default"
            fullWidth
            classNames={QUICK}
            leftSection={<IconShoppingCart size={16} />}
            onClick={onRequisition}
          >
            {t('hse.hseq.quick.createRequisition')}
          </Button>
          <Button
            variant="default"
            fullWidth
            classNames={QUICK}
            leftSection={<IconExternalLink size={16} />}
            onClick={onSms}
          >
            {t('hse.hseq.quick.smsUpdate')}
          </Button>
          <Button
            variant="default"
            fullWidth
            classNames={QUICK}
            leftSection={<IconCopy size={16} />}
            onClick={onCopy}
          >
            {t('hse.hseq.quick.copy')}
          </Button>
          {closable || closeGaps.length === 0 ? (
            close
          ) : (
            <Tooltip
              label={t('hse.hseq.quick.closeBlocked', { gaps: closeGaps.join('; ') })}
              multiline
              w={260}
            >
              <span>{close}</span>
            </Tooltip>
          )}
        </div>
      )}
    </Panel>
  );
}

/* ---------- Parçalar ---------- */

/** PSC Remark Details (BGM): No, Issue / Remark, Category, Regulation Ref., Due Date, Status, Action. */
function FindingsCard({
  detail,
  canEdit,
  onAdd,
  onView,
}: {
  detail: HseDetail;
  canEdit: boolean;
  onAdd: () => void;
  onView: (finding: HseFinding) => void;
}) {
  const { t } = useTranslation();
  return (
    <Panel
      title={t('hse.findings.title')}
      icon={<IconFileDescription size={18} />}
      extra={
        canEdit ? (
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={onAdd}>
            {t('hse.findings.add')}
          </Button>
        ) : null
      }
      flush
    >
      <div className={classes.cardTable}>
        <p className={classes.muted}>{t('hse.findings.subtitle')}</p>
        {detail.findings.length === 0 ? (
          <p className={classes.muted}>{t('hse.findings.empty')}</p>
        ) : (
          <section
            className={table.tableWrap}
            // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
            tabIndex={0}
            aria-label={t('hse.findings.subtitle')}
          >
            <table className={`${table.table} ${classes.table}`}>
              <thead>
                <tr>
                  <th scope="col">{t('hse.findings.no')}</th>
                  <th scope="col">{t('hse.findings.issue')}</th>
                  <th scope="col">{t('hse.findings.category')}</th>
                  <th scope="col">{t('hse.findings.regulation')}</th>
                  <th scope="col">{t('hse.findings.dueDate')}</th>
                  <th scope="col">{t('hse.findings.status')}</th>
                  <th scope="col">{t('hse.findings.action')}</th>
                </tr>
              </thead>
              <tbody>
                {detail.findings.map((finding) => (
                  <tr key={finding.id}>
                    <td>{finding.itemNo}</td>
                    <td className={classes.wrap}>{finding.description}</td>
                    <td>{finding.category ?? '—'}</td>
                    <td>{finding.regulationRef ?? '—'}</td>
                    <td>{formatDay(finding.dueDate)}</td>
                    <td>
                      <HseStepBadge status={finding.status} kind="finding" />
                    </td>
                    <td>
                      <button
                        type="button"
                        className={classes.link}
                        onClick={() => onView(finding)}
                      >
                        {t(canEdit ? 'hse.findings.edit' : 'hse.findings.view')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
      </div>
    </Panel>
  );
}

/** Corrective Actions (BGM): Action Plan / Progress / Closure. */
function ActionsCard({
  detail,
  source,
  busy,
  onAdd,
  onEdit,
  onStart,
  onComplete,
  onCancel,
  onUpload,
  onClosure,
}: {
  detail: HseDetail;
  source: HseSource;
  busy: boolean;
  onAdd: () => void;
  onEdit: (action: HseAction) => void;
  onStart: (action: HseAction) => void;
  onComplete: (action: HseAction) => void;
  onCancel: (action: HseAction) => void;
  onUpload: (action: HseAction, file: File) => void;
  onClosure: () => void;
}) {
  const { t } = useTranslation();
  const editor = source.editor;
  const mine = new Set(detail.can.actionIds ?? []);
  return (
    <Panel
      title={t('hse.actions.title')}
      icon={<IconTool size={18} />}
      extra={
        editor && detail.can.actions ? (
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={onAdd}>
            {t('hse.actions.new')}
          </Button>
        ) : null
      }
      flush
    >
      <Tabs
        defaultValue="plan"
        className={classes.innerTabs}
        classNames={{ list: classes.tabsList }}
      >
        <Tabs.List>
          <Tabs.Tab value="plan">{t('hse.actions.plan')}</Tabs.Tab>
          <Tabs.Tab value="progress">{t('hse.actions.progress')}</Tabs.Tab>
          <Tabs.Tab value="closure">{t('hse.actions.closure')}</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="plan" pt="sm">
          {detail.actionList.length === 0 ? (
            <p className={classes.muted}>
              {t(detail.report.reviewedAt ? 'hse.actions.empty' : 'hse.actions.afterReview')}
            </p>
          ) : (
            <section
              className={table.tableWrap}
              // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
              tabIndex={0}
              aria-label={t('hse.actions.plan')}
            >
              <table className={`${table.table} ${classes.table}`}>
                <thead>
                  <tr>
                    <th scope="col">{t('hse.actions.no')}</th>
                    <th scope="col">{t('hse.actions.description')}</th>
                    <th scope="col">{t('hse.actions.responsible')}</th>
                    <th scope="col">{t('hse.actions.dueDate')}</th>
                    <th scope="col">{t('hse.actions.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.actionList.map((action) => (
                    <tr key={action.id}>
                      <td>{action.actionNo}</td>
                      <td className={classes.wrap}>
                        {editor && mine.has(action.id) && action.status === 'planned' ? (
                          <button
                            type="button"
                            className={classes.link}
                            onClick={() => onEdit(action)}
                          >
                            {action.description}
                          </button>
                        ) : (
                          action.description
                        )}
                        <div className={classes.muted}>{t(`hse.actionKinds.${action.kind}`)}</div>
                      </td>
                      <td>{action.responsible}</td>
                      <td>
                        {formatDay(action.dueDate)}
                        {action.overdue ? (
                          <div className={classes.muted}>{t('hse.list.overdue')}</div>
                        ) : null}
                      </td>
                      <td>
                        <HseStepBadge status={action.status} kind="action" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </Tabs.Panel>
        <Tabs.Panel value="progress" pt="sm">
          {detail.actionList.length === 0 ? (
            <p className={classes.muted}>{t('hse.actions.empty')}</p>
          ) : (
            <ul className={classes.timeline}>
              {detail.actionList.map((action) => (
                <ActionProgress
                  key={action.id}
                  action={action}
                  canRun={Boolean(editor && mine.has(action.id))}
                  busy={busy}
                  names={detail.names}
                  onStart={() => onStart(action)}
                  onComplete={() => onComplete(action)}
                  onCancel={() => onCancel(action)}
                  onUpload={(file) => onUpload(action, file)}
                  fileUrl={source.office ? (id) => source.office?.fileUrl(id) ?? '' : undefined}
                />
              ))}
            </ul>
          )}
        </Tabs.Panel>
        <Tabs.Panel value="closure" pt="sm">
          <ClosureChecklist detail={detail} />
          {editor && detail.can.closure ? (
            <Button mt="sm" onClick={onClosure}>
              {t('hse.closure.submit')}
            </Button>
          ) : null}
        </Tabs.Panel>
      </Tabs>
    </Panel>
  );
}

function FilePicker({
  label,
  onFile,
  disabled,
}: {
  label: string;
  onFile: (file: File) => void;
  disabled?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={input}
        type="file"
        accept="image/jpeg,image/png,application/pdf"
        hidden
        onChange={(event) => {
          const file = event.currentTarget.files?.[0];
          event.currentTarget.value = '';
          if (file) onFile(file);
        }}
      />
      <Button
        size="xs"
        variant="light"
        leftSection={<IconCloudUpload size={14} />}
        disabled={disabled ?? false}
        onClick={() => input.current?.click()}
      >
        {label}
      </Button>
    </>
  );
}

function ActionProgress({
  action,
  canRun,
  busy,
  names,
  onStart,
  onComplete,
  onCancel,
  onUpload,
  fileUrl,
}: {
  action: HseAction;
  canRun: boolean;
  busy: boolean;
  names: Record<string, string>;
  onStart: () => void;
  onComplete: () => void;
  onCancel: () => void;
  onUpload: (file: File) => void;
  fileUrl: ((id: string) => string) | undefined;
}) {
  const { t } = useTranslation();
  const open = action.status === 'planned' || action.status === 'in_progress';
  return (
    <li>
      <span>
        <strong>#{action.actionNo}</strong> <HseStepBadge status={action.status} kind="action" />
      </span>
      <div>
        <div>{action.description}</div>
        <div className={classes.timelineDetail}>
          {action.startedAt
            ? `${t('hse.actions.started')}: ${formatDateTime(action.startedAt)} · `
            : ''}
          {action.completedAt
            ? `${t('hse.actions.completed')}: ${formatDateTime(action.completedAt)} (${names[action.completedBy ?? ''] ?? ''})`
            : ''}
          {action.completionNote ? `\n${action.completionNote}` : ''}
          {action.cancelReason ? `\n${t('hse.actions.cancelled')}: ${action.cancelReason}` : ''}
        </div>
        {action.files.length > 0 ? (
          <ul className={classes.files}>
            {action.files.map((file) => (
              <HseFileItem key={file.id} file={file} href={fileUrl?.(file.id)} />
            ))}
          </ul>
        ) : open ? (
          <div className={classes.muted}>{t('hse.actions.noEvidence')}</div>
        ) : null}
        {canRun && open ? (
          <div className={classes.rowActions}>
            {action.status === 'planned' ? (
              <Button size="xs" variant="light" disabled={busy} onClick={onStart}>
                {t('hse.actions.start')}
              </Button>
            ) : null}
            <FilePicker label={t('hse.actions.addEvidence')} disabled={busy} onFile={onUpload} />
            <Button size="xs" disabled={busy} onClick={onComplete}>
              {t('hse.actions.complete')}
            </Button>
            <Button size="xs" variant="subtle" color="red" disabled={busy} onClick={onCancel}>
              {t('hse.actions.cancel')}
            </Button>
          </div>
        ) : null}
      </div>
    </li>
  );
}

function ClosureChecklist({ detail }: { detail: HseDetail }) {
  const { t } = useTranslation();
  const gaps = new Set(detail.gaps);
  return (
    <ul className={classes.checklist} aria-label={t('hse.closure.checklist')}>
      {closureChecks(detail.reportType).map((gap) => (
        <li key={gap} data-ok={!gaps.has(gap) || undefined}>
          <CheckMark ok={!gaps.has(gap)} />
          {t(`hse.closure.checks.${gap}`)}
        </li>
      ))}
    </ul>
  );
}

/** Kapanış onay adımları ve kararlar (9. Management / DPA Review). */
function ApprovalSteps({ detail }: { detail: HseDetail }) {
  const { t } = useTranslation();
  const steps = detail.approval.steps;
  if (steps.length === 0) return <p className={classes.muted}>{t('hse.hseq.review.noSteps')}</p>;
  return (
    <div>
      <div className={classes.label}>{t('hse.closure.steps')}</div>
      <ol className={classes.checklist}>
        {steps.map((step, index) => {
          const decision = [...detail.approval.decisions]
            .reverse()
            .find(
              (d) =>
                d.step === index + 1 && d.submissionId === detail.approval.submissions.at(-1)?.id,
            );
          return (
            <li
              // biome-ignore lint/suspicious/noArrayIndexKey: adım sırası kuralın parçasıdır
              key={`${step.roleId}-${index}`}
              data-ok={decision?.decision === 'approved' || undefined}
            >
              <CheckMark ok={decision?.decision === 'approved'} />
              {index + 1}. {detail.roles[step.roleId] ?? step.roleId}
              {step.office ? ` (${t('hse.closure.office')})` : ''}
              {decision
                ? ` — ${t(`hse.closure.decisions.${decision.decision}`)} · ${detail.names[decision.by] ?? ''} · ${formatDateTime(decision.at)}`
                : detail.pending?.step === index + 1
                  ? ` — ${t('hse.closure.waiting')}`
                  : ''}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/** 10. Closure: koşullar, kapanışa gönderim, kapandı / yeniden açıldı, yeniden açma. */
function ClosurePanel({
  detail,
  dirty,
  onClosure,
  onReopen,
}: {
  detail: HseDetail;
  dirty: boolean;
  onClosure: () => void;
  onReopen: (() => void) | undefined;
}) {
  const { t } = useTranslation();
  return (
    <div className={classes.cardBody}>
      <ClosureChecklist detail={detail} />
      {detail.approval.state === 'approved' ? (
        <p className={classes.banner} data-tone="success">
          {t('hse.closure.closedBanner')}
        </p>
      ) : null}
      {detail.status === 'reopened' ? (
        <p className={classes.banner} data-tone="danger">
          {t('hse.closure.reopenedBanner')}
        </p>
      ) : null}
      {/* Kapanışın iadesi ya da yeniden açılması (ADR 0116 Ek): kim, ne zaman, gerekçe */}
      <ReturnedBanner
        notice={detail.returnNotice}
        offsetMin={detail.report.tzOffsetMin}
        hint={Boolean(detail.can.closure)}
      />
      <Group>
        {detail.can.closure && !dirty ? (
          <Button onClick={onClosure}>{t('hse.closure.submit')}</Button>
        ) : null}
        {dirty && detail.can.closure ? (
          <span className={classes.muted}>{t('hse.closure.saveFirst')}</span>
        ) : null}
        {onReopen ? (
          <Button variant="default" color="red" onClick={onReopen}>
            {t('hse.closure.reopen')}
          </Button>
        ) : null}
      </Group>
    </div>
  );
}

/** Photos / Documents (BGM sağ sütun): raporun ekleri; gemide yükleme. */
function PhotosCard({
  detail,
  source,
  busy,
  onUpload,
}: {
  detail: HseDetail | null;
  source: HseSource;
  busy: boolean;
  onUpload: (file: File) => void;
}) {
  const { t } = useTranslation();
  const canUpload = Boolean(source.editor && detail?.can.attach);
  return (
    <Panel title={t('hse.files.title')} icon={<IconPhoto size={18} />}>
      <div className={classes.cardBody}>
        {source.editor ? (
          <div className={classes.drop}>
            <IconCloudUpload size={24} aria-hidden />
            <span>{t(detail ? 'hse.files.drop' : 'hse.files.afterSave')}</span>
            <span className={classes.muted}>{t('hse.files.formats')}</span>
            {canUpload ? (
              <FilePicker label={t('hse.files.choose')} disabled={busy} onFile={onUpload} />
            ) : null}
          </div>
        ) : null}
        {detail && detail.files.length > 0 ? (
          <ul className={classes.files}>
            {detail.files.map((file) => (
              <HseFileItem key={file.id} file={file} href={source.office?.fileUrl(file.id)} />
            ))}
          </ul>
        ) : (
          <p className={classes.muted}>{t('hse.files.empty')}</p>
        )}
      </div>
    </Panel>
  );
}

function AttachmentsTab({ detail, source }: { detail: HseDetail | null; source: HseSource }) {
  const { t } = useTranslation();
  if (!detail) return <p className={classes.muted}>{t('hse.files.afterSave')}</p>;
  const groups = [
    { key: 'report', title: t('hse.files.reportFiles'), files: detail.files },
    ...detail.actionList.map((a) => ({
      key: a.id,
      title: `${t('hse.files.actionFiles')} #${a.actionNo} · ${a.description}`,
      files: a.files,
    })),
  ];
  return (
    <div className={classes.main}>
      {groups.map((group) => (
        <Panel key={group.key} title={group.title}>
          {group.files.length === 0 ? (
            <p className={classes.muted}>{t('hse.files.empty')}</p>
          ) : (
            <ul className={classes.files}>
              {group.files.map((file) => (
                <HseFileItem key={file.id} file={file} href={source.office?.fileUrl(file.id)} />
              ))}
            </ul>
          )}
        </Panel>
      ))}
    </div>
  );
}

function CommentsTab({
  detail,
  canComment,
  onComment,
}: {
  detail: HseDetail | null;
  canComment: boolean;
  onComment: (body: string) => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const [body, setBody] = useState('');
  if (!detail) return <p className={classes.muted}>{t('hse.comments.afterSubmit')}</p>;
  const entries = [
    ...detail.comments.map((c) => ({ id: c.id, at: c.at, by: c.by, body: c.body, kind: 'ship' })),
    ...detail.officeNotes.map((n) => ({
      id: n.id,
      at: n.at,
      by: n.by,
      body: n.body,
      kind: n.kind,
    })),
  ].sort((a, b) => a.at.localeCompare(b.at));
  return (
    <Panel title={t('hse.comments.title')}>
      <div className={classes.cardBody}>
        {entries.length === 0 ? (
          <EmptyState title={t('hse.comments.empty')} />
        ) : (
          <ul className={classes.timeline}>
            {entries.map((entry) => (
              <li key={entry.id}>
                <span>
                  {formatDateTime(entry.at)}
                  <div className={classes.muted}>{detail.names[entry.by] ?? ''}</div>
                </span>
                <div>
                  <strong>
                    {t(entry.kind === 'ship' ? 'hse.comments.ship' : `hse.noteKinds.${entry.kind}`)}
                  </strong>
                  <div className={classes.timelineDetail}>{entry.body}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
        {canComment ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (!body.trim()) return;
              void onComment(body.trim())
                .then(() => setBody(''))
                .catch(() => undefined);
            }}
          >
            <Textarea
              label={t('hse.comments.add')}
              value={body}
              autosize
              minRows={2}
              maxLength={4000}
              onChange={(event) => setBody(event.currentTarget.value)}
            />
            <Button type="submit" mt="sm" disabled={!body.trim()}>
              {t('hse.comments.send')}
            </Button>
          </form>
        ) : null}
      </div>
    </Panel>
  );
}

function HistoryTab({ detail }: { detail: HseDetail | null }) {
  const { t } = useTranslation();
  if (!detail) return <p className={classes.muted}>{t('hse.history.empty')}</p>;
  return (
    <Panel title={t('hse.history.title')}>
      {detail.history.length === 0 ? (
        <p className={classes.muted}>{t('hse.history.empty')}</p>
      ) : (
        <ul className={classes.timeline}>
          {detail.history.map((entry, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: geçmiş satırının kimliği yok; sıra sabit
            <li key={`${entry.at}-${entry.event}-${index}`}>
              <span>
                {formatDateTime(entry.at)}
                <div className={classes.muted}>
                  {entry.by ? (detail.names[entry.by] ?? '') : ''}
                </div>
              </span>
              <div>
                <strong>
                  {t(`hse.history.events.${entry.event}`, { defaultValue: entry.event })}
                </strong>
                {entry.detail ? <div className={classes.timelineDetail}>{entry.detail}</div> : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function RelatedReports({
  detail,
  canCreate,
  onOpen,
  onNewChild,
}: {
  detail: HseDetail | null;
  canCreate: boolean;
  onOpen: (id: string) => void;
  onNewChild?: (parentId: string) => void;
}) {
  const { t } = useTranslation();
  const children = detail?.related.children ?? [];
  return (
    <Panel
      title={t('hse.related.title')}
      extra={
        canCreate && detail && onNewChild ? (
          <Button
            size="xs"
            leftSection={<IconPlus size={14} />}
            onClick={() => onNewChild(detail.id)}
          >
            {t('hse.related.newDeficiency')}
          </Button>
        ) : null
      }
    >
      <div className={classes.cardBody}>
        <p className={classes.muted}>{t('hse.related.subtitle')}</p>
        {children.length === 0 ? (
          <p className={classes.muted}>{t('hse.related.empty')}</p>
        ) : (
          <ul className={classes.links}>
            {children.map((child) => (
              <li key={child.id}>
                <span className={classes.linkIcon} aria-hidden>
                  <IconLink size={16} />
                </span>
                <button type="button" className={classes.link} onClick={() => onOpen(child.id)}>
                  {child.reportNo} · {child.title}
                </button>
                <span className={classes.muted}>{t(`hse.types.${child.reportType}`)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Panel>
  );
}

/**
 * Kaza bildirimleri (ADR 0049 §10; H8): alıcı, ad, son gün, gönderildiği gün, yol, referans.
 * İlk değerlendirmede durur: soruşturmanın bitmesini beklemez.
 */
function Notifications({
  value,
  onChange,
  readOnly,
}: {
  value: HseReportInput;
  onChange: (next: HseReportInput) => void;
  readOnly: boolean;
}) {
  const { t } = useTranslation();
  const items =
    (getPath(value, 'details.notification.items') as
      | {
          recipient: string;
          name: string | null;
          dueDate?: string | null;
          notifiedAt: string | null;
          method: string | null;
          reference: string | null;
        }[]
      | undefined) ?? [];
  const set = (next: typeof items) => onChange(setPath(value, 'details.notification.items', next));
  return (
    <div className={classes.cardBody}>
      {items.map((item, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: bildirim satırının kimliği sırasıdır
        <div key={index} className={classes.grid3}>
          <Select
            label={t('hse.notifications.recipient')}
            data={NOTIFICATION_RECIPIENTS.map((r) => ({
              value: r,
              label: t(`hse.recipients.${r}`),
            }))}
            value={item.recipient}
            readOnly={readOnly}
            allowDeselect={false}
            onChange={(recipient) =>
              set(
                items.map((it, i) =>
                  i === index ? { ...it, recipient: recipient ?? it.recipient } : it,
                ),
              )
            }
          />
          <TextInput
            label={t('hse.notifications.name')}
            value={item.name ?? ''}
            readOnly={readOnly}
            onChange={(event) => {
              const name = event.currentTarget.value || null;
              set(items.map((it, i) => (i === index ? { ...it, name } : it)));
            }}
          />
          <DayInput
            label={t('hse.notifications.dueDate')}
            value={item.dueDate ?? null}
            readOnly={readOnly}
            onChange={(day) =>
              set(items.map((it, i) => (i === index ? { ...it, dueDate: day } : it)))
            }
          />
          <DayInput
            label={t('hse.notifications.notifiedOn')}
            value={item.notifiedAt ? item.notifiedAt.slice(0, 10) : null}
            readOnly={readOnly}
            onChange={(day) =>
              set(
                items.map((it, i) =>
                  i === index ? { ...it, notifiedAt: day ? `${day}T00:00:00.000Z` : null } : it,
                ),
              )
            }
          />
          <TextInput
            label={t('hse.notifications.method')}
            value={item.method ?? ''}
            readOnly={readOnly}
            onChange={(event) => {
              const method = event.currentTarget.value || null;
              set(items.map((it, i) => (i === index ? { ...it, method } : it)));
            }}
          />
          <TextInput
            label={t('hse.notifications.reference')}
            value={item.reference ?? ''}
            readOnly={readOnly}
            onChange={(event) => {
              const reference = event.currentTarget.value || null;
              set(items.map((it, i) => (i === index ? { ...it, reference } : it)));
            }}
          />
          {!readOnly ? (
            <Button
              variant="subtle"
              color="red"
              mt="lg"
              onClick={() => set(items.filter((_, i) => i !== index))}
            >
              {t('hse.notifications.remove')}
            </Button>
          ) : null}
        </div>
      ))}
      {!readOnly ? (
        <Button
          variant="light"
          size="xs"
          leftSection={<IconPlus size={14} />}
          onClick={() =>
            set([
              ...items,
              {
                recipient: 'flag_state',
                name: null,
                dueDate: null,
                notifiedAt: null,
                method: null,
                reference: null,
              },
            ])
          }
        >
          {t('hse.notifications.add')}
        </Button>
      ) : items.length === 0 ? (
        <p className={classes.muted}>{t('hse.notifications.empty')}</p>
      ) : null}
    </div>
  );
}
